import { useState, useEffect, useRef } from "react";
import { fetchOverviewData } from "./apiService";
import React from "react";

const mergeAndDeduplicateData = (existingData, newData, reportType) => {
  // If no existing data, just return new data
  if (!existingData || existingData.length === 0) {
    return newData || [];
  }

  // If no new data, return existing
  if (!newData || newData.length === 0) {
    return existingData;
  }

  if (reportType === "trip") {
    // For trips, just concatenate and deduplicate by unique trip identifiers
    const combined = [...existingData, ...newData];
    const seen = new Map();

    return combined.filter(item => {
      const key = `${item.vehicleNumber || item.vehicleName}_${item.routeName}_${item.date}_${item.startTime}_${item.endTime}_${item.duration}`;
      if (seen.has(key)) return false;
      seen.set(key, true);
      return true;
    });
  }

  else if (reportType === "halt") {
    // For halts, concatenate and deduplicate
    const combined = [...existingData, ...newData];
    const seen = new Map();

    return combined.filter(item => {
      const key = `${item.vehicleNumber}_${item.startTime}_${item.endTime}_${item.duration || ''}`;
      if (seen.has(key)) return false;
      seen.set(key, true);
      return true;
    });
  }

  else if (reportType === "overspeed") {
    // For overspeed, concatenate and deduplicate
    const combined = [...existingData, ...newData];
    const seen = new Map();

    return combined.filter(item => {
      const key = `${item.deviceId}_${item.timestamp || item.time}_${item.speed}`;
      if (seen.has(key)) return false;
      seen.set(key, true);
      return true;
    });
  }

  else if (reportType === "runtime" || reportType === "activity") {
    // For runtime/activity, aggregate by vehicle
    const vehicleMap = new Map();

    // First, add all existing data to map
    existingData.forEach(item => {
      const vehicleKey = item.deviceId || item.vehicleNumber || item.vehicleName;
      if (vehicleKey) {
        vehicleMap.set(vehicleKey, {
          ...item,
          _speedSum: parseFloat(item.averageSpeed) || 0,
          _speedCount: (parseFloat(item.averageSpeed) || 0) > 0 ? 1 : 0
        });
      }
    });

    // Then merge new data into existing vehicles
    newData.forEach(item => {
      const vehicleKey = item.deviceId || item.vehicleNumber || item.vehicleName;
      if (!vehicleKey) return;

      if (vehicleMap.has(vehicleKey)) {
        // Vehicle exists, aggregate the values
        const existing = vehicleMap.get(vehicleKey);

        // Sum runtime hours
        existing.totalMovingHours = (parseFloat(existing.totalMovingHours) || 0) + (parseFloat(item.totalMovingHours) || 0);

        // Sum distance
        existing.noOfKmsCovered = (parseFloat(existing.noOfKmsCovered) || 0) + (parseFloat(item.noOfKmsCovered) || 0);

        // Sum route deviations
        existing.outOfRouteCount = (parseInt(existing.outOfRouteCount) || 0) + (parseInt(item.outOfRouteCount) || 0);

        // Average speed calculation
        if (item.averageSpeed && parseFloat(item.averageSpeed) > 0) {
          existing._speedSum = (existing._speedSum || 0) + parseFloat(item.averageSpeed);
          existing._speedCount = (existing._speedCount || 0) + 1;
          existing.averageSpeed = existing._speedSum / existing._speedCount;
        }

        // Max stops (not sum)
        existing.noOfStopsCovered = Math.max(
          parseInt(existing.noOfStopsCovered) || 0,
          parseInt(item.noOfStopsCovered) || 0
        );

        vehicleMap.set(vehicleKey, existing);
      } else {
        // New vehicle, add it
        vehicleMap.set(vehicleKey, {
          ...item,
          _speedSum: parseFloat(item.averageSpeed) || 0,
          _speedCount: (parseFloat(item.averageSpeed) || 0) > 0 ? 1 : 0
        });
      }
    });

    // Convert back to array and clean up
    return Array.from(vehicleMap.values()).map(item => {
      const cleaned = { ...item };
      delete cleaned._speedSum;
      delete cleaned._speedCount;

      // Round values
      if (cleaned.averageSpeed) {
        cleaned.averageSpeed = Math.round(cleaned.averageSpeed * 100) / 100;
      }
      if (cleaned.totalMovingHours) {
        cleaned.totalMovingHours = Math.round(cleaned.totalMovingHours * 100) / 100;
      }
      if (cleaned.noOfKmsCovered) {
        cleaned.noOfKmsCovered = Math.round(cleaned.noOfKmsCovered * 100) / 100;
      }

      return cleaned;
    });
  }

  else {
    // Default: just concatenate
    return [...existingData, ...newData];
  }
};

export function useProgressiveOverviewData(selectedDateRange, customDateRange) {
  const [chartDataStates, setChartDataStates] = useState({});
  const [error, setError] = useState(null);
  const [loadingStates, setLoadingStates] = useState({});
  const [chunkProgress, setChunkProgress] = useState({});
  const [isCancelling, setIsCancelling] = useState(false);

  // Use refs to manage abort controllers and fetch state
  const abortControllersRef = useRef({});
  const fetchingRef = useRef(false);
  const lastFetchParamsRef = useRef(null);
  const currentFetchIdRef = useRef(0);

  useEffect(() => {
    // Early return if selectedDateRange is null
    if (!selectedDateRange) {
      return;
    }

    // Create a unique key for the current fetch parameters
    const currentFetchKey = `${selectedDateRange}_${customDateRange?.startDate}_${customDateRange?.endDate}`;

    // Check if date range has changed
    const dateRangeChanged = lastFetchParamsRef.current !== currentFetchKey;

    if (dateRangeChanged && fetchingRef.current) {
      console.log('🔄 Date range changed - cancelling previous requests');
      
      // Set cancelling state
      setIsCancelling(true);
      
      // Cancel all in-flight requests
      Object.keys(abortControllersRef.current).forEach(key => {
        if (abortControllersRef.current[key]) {
          console.log(`❌ Aborting request for: ${key}`);
          abortControllersRef.current[key].abort();
        }
      });
      
      // Clear abort controllers
      abortControllersRef.current = {};
      
      // Reset states
      setChartDataStates({});
      setLoadingStates({});
      setChunkProgress({});
      setError(null);
      
      // Allow new fetch to proceed
      fetchingRef.current = false;
      
      // Clear cancelling state after a brief delay
      setTimeout(() => setIsCancelling(false), 300);
    }

    // Skip if same parameters (and not cancelled)
    if (!dateRangeChanged && lastFetchParamsRef.current === currentFetchKey) {
      return;
    }

    const initializeAndFetchData = async () => {
      // Increment fetch ID to track this specific fetch operation
      const fetchId = ++currentFetchIdRef.current;
      
      fetchingRef.current = true;
      lastFetchParamsRef.current = currentFetchKey;
      setError(null);

      try {
        const dataFetchers = await fetchOverviewData(selectedDateRange, customDateRange);

        // Check if this fetch was cancelled
        if (fetchId !== currentFetchIdRef.current) {
          console.log('⚠️ Fetch cancelled - newer fetch in progress');
          return;
        }

        // Initialize states
        const initialStates = {};
        const initialLoadingStates = {};
        const initialChunkProgress = {};

        dataFetchers.forEach(({ key, totalChunks }) => {
          initialStates[key] = {
            loading: true,
            data: null,
            error: null
          };
          initialLoadingStates[key] = {
            isLoadingMore: true,
            chunksLoaded: 0,
            totalChunks: totalChunks
          };
          initialChunkProgress[key] = {
            current: 0,
            total: totalChunks,
            percentComplete: 0
          };
        });

        setChartDataStates(initialStates);
        setLoadingStates(initialLoadingStates);
        setChunkProgress(initialChunkProgress);

        // Fetch chunks sequentially for each data type
        const fetchChunksSequentially = async (fetcher, key) => {
          let accumulatedData = [];
          let maxSpeed = null;
          const reportType = key.split('_')[0];

          // Create abort controller for this data stream
          const abortController = new AbortController();
          abortControllersRef.current[key] = abortController;

          try {
            for (let chunkIndex = 0; chunkIndex < fetcher.totalChunks; chunkIndex++) {
              // Check if this fetch was cancelled
              if (abortController.signal.aborted || fetchId !== currentFetchIdRef.current) {
                console.log(`⚠️ Chunk fetch aborted for ${key} at chunk ${chunkIndex}`);
                return;
              }

              const chunkResult = await fetcher.fetchNextChunk(chunkIndex);

              // Double-check abort status after async operation
              if (abortController.signal.aborted || fetchId !== currentFetchIdRef.current) {
                console.log(`⚠️ Chunk fetch aborted for ${key} after fetch`);
                return;
              }

              if (chunkResult && chunkResult.data) {
                // Merge new chunk data with accumulated data
                accumulatedData = mergeAndDeduplicateData(
                  accumulatedData,
                  chunkResult.data,
                  reportType
                );

                if (chunkResult.maxSpeed) {
                  maxSpeed = chunkResult.maxSpeed;
                }

                const progress = ((chunkIndex + 1) / fetcher.totalChunks) * 100;

                // Only update state if not aborted
                if (!abortController.signal.aborted && fetchId === currentFetchIdRef.current) {
                  // Update progress
                  setChunkProgress(prev => ({
                    ...prev,
                    [key]: {
                      current: chunkIndex + 1,
                      total: fetcher.totalChunks,
                      percentComplete: Math.round(progress)
                    }
                  }));

                  // Update chart data with accumulated data
                  setChartDataStates(prev => ({
                    ...prev,
                    [key]: {
                      loading: false,
                      data: {
                        data: accumulatedData,
                        success: true,
                        maxSpeed: maxSpeed
                      },
                      error: null
                    }
                  }));

                  // Update loading state
                  setLoadingStates(prev => ({
                    ...prev,
                    [key]: {
                      isLoadingMore: chunkIndex < fetcher.totalChunks - 1,
                      chunksLoaded: chunkIndex + 1,
                      totalChunks: fetcher.totalChunks
                    }
                  }));
                }
              }

              // Small delay between chunks to prevent overwhelming the API
              if (chunkIndex < fetcher.totalChunks - 1) {
                await new Promise(resolve => setTimeout(resolve, 200));
              }
            }
          } catch (err) {
            // Only log and update state if not aborted
            if (!abortController.signal.aborted && fetchId === currentFetchIdRef.current) {
              console.error(`Error fetching chunks for ${key}:`, err);
              setChartDataStates(prev => ({
                ...prev,
                [key]: { loading: false, data: null, error: err.message }
              }));
            }
          } finally {
            // Clean up abort controller
            if (abortControllersRef.current[key] === abortController) {
              delete abortControllersRef.current[key];
            }

            // Only update final loading state if not aborted
            if (!abortController.signal.aborted && fetchId === currentFetchIdRef.current) {
              setLoadingStates(prev => ({
                ...prev,
                [key]: {
                  isLoadingMore: false,
                  chunksLoaded: fetcher.totalChunks,
                  totalChunks: fetcher.totalChunks
                }
              }));
            }
          }
        };

        // Start all fetchers in parallel
        await Promise.all(
          dataFetchers.map(fetcher => fetchChunksSequentially(fetcher, fetcher.key))
        );

      } catch (err) {
        // Only update error state if this fetch is still current
        if (fetchId === currentFetchIdRef.current) {
          console.error("Error setting up overview data fetch:", err);
          setError("Failed to initialize data fetching");
        }
      } finally {
        // Only clear fetching flag if this is the current fetch
        if (fetchId === currentFetchIdRef.current) {
          fetchingRef.current = false;
        }
      }
    };

    initializeAndFetchData();

    // Cleanup function - cancel requests when component unmounts or deps change
    return () => {
      Object.keys(abortControllersRef.current).forEach(key => {
        if (abortControllersRef.current[key]) {
          abortControllersRef.current[key].abort();
        }
      });
      abortControllersRef.current = {};
    };
  }, [selectedDateRange, customDateRange]);

  // Return empty states if selectedDateRange is null
  if (!selectedDateRange) {
    return {
      chartDataStates: {},
      loadingStates: {},
      error: null,
      chunkProgress: {},
      isCancelling: false
    };
  }

  return { 
    chartDataStates, 
    loadingStates, 
    error, 
    chunkProgress,
    isCancelling 
  };
};

export const ChunkLoadingIndicator = ({ progress }) => {
  if (!progress || progress.percentComplete >= 100) return null;

  return (
    <div style={{
      position: 'absolute',
      top: '8px',
      right: '8px',
      zIndex: 10,
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      backgroundColor: 'rgba(255, 255, 255, 0.95)',
      padding: '4px 8px',
      borderRadius: '12px',
      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
      fontSize: '11px',
      color: '#666'
    }}>
      <div style={{
        width: '14px',
        height: '14px',
        border: '2px solid #f3f3f3',
        borderTop: '2px solid #3498db',
        borderRadius: '50%',
        animation: 'spin 0.8s linear infinite'
      }} />
      <span>{progress.percentComplete}%</span>
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};